// src/App.jsx
import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import ProtectedRoute from './components/ProtectedRoute';
import ClientCRM from './components/ClientCRM.jsx';
import WinnieTracker from './components/WinnieTracker.jsx';
import WinnieReport from './components/WinnieReport';

// Page Imports
import HomePage from './pages/HomePage.jsx';
import AboutPage from './pages/AboutPage.jsx';
import ContactPage from './pages/ContactPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import SignUpPage from './pages/SignUpPage.jsx';
import PricingOptionsPage from './pages/PricingOptionsPage.jsx';
import TeacherDashboard from './pages/TeacherDashboard.jsx';
import StudentDashboard from './pages/StudentDashboard.jsx';
import PartnerDashboard from './pages/PartnerDashboard.jsx';
import PartnerSignup from './pages/PartnerSignup';
import CommunityInvestment from './pages/CommunityInvestment'; 
import CrowdfundingDashboard from './pages/CrowdfundingDashboard.jsx';
import BorealisPage from './pages/BorealisPage'; 
import DataRoomPage from './pages/DataRoomPage';
import LeaderLab from './pages/LeaderLab.jsx';
import CustomTrackForm from './pages/CustomTrackForm.jsx';
import CustomTrackQuiz from './pages/CustomTrackQuiz.jsx';
import YoungGeniusForm from './pages/YoungGeniusForm.jsx';
import YoungGeniusQuizPage from './pages/YoungGeniusQuizPage.jsx';
import LexiconPage from './pages/LexiconPage.jsx';
import ArgotPage from './pages/ArgotPage.jsx';
import ArgotPractice from './pages/ArgotPractice.jsx';
import LexiconPractice from './pages/LexiconPractice.jsx';

// PERSONAL TRACKER IMPORTS
import PersonalPathway from './pages/PersonalPathway.jsx';
import PersonalChoreScheduler from './pages/PersonalChoreScheduler.jsx';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ArgotCard from './components/content-wrappers/ArgotCard.jsx';

// GAME IMPORTS
import AutarkyGameEngine from './components/game/AutarkyGameEngine.jsx';
import VeridianStart from './components/game/veridian/VeridianStart.jsx';
import VeridianEngine from './components/game/veridian/VeridianEngine.jsx';

// COURSE IMPORTS
import BankingDemoSimulator from './pages/BankingDemoSimulator.jsx';
import BankingExamSimulator from './pages/BankingExamSimulator.jsx';
import BankingPathways from './pages/BankingPathways.jsx';
import BankingScenario from './pages/BankingScenario.jsx';
import BankingUnit1 from './pages/BankingUnit1.jsx';

import LogisticsDemoSimulator from './pages/LogisticsDemoSimulator.jsx';
import LogisticsExamSimulator from './pages/LogisticsExamSimulator.jsx';
import LogisticsPathways from './pages/LogisticsPathways.jsx';
import LogisticsScenario from './pages/LogisticsScenario.jsx';
import LogisticsUnit1 from './pages/LogisticsUnit1.jsx';

import MiningDemoSimulator from './pages/MiningDemoSimulator.jsx';
import MiningExamSimulator from './pages/MiningExamSimulator.jsx';
import MiningPathways from './pages/MiningPathways.jsx';
import MiningScenario from './pages/MiningScenario.jsx';
import MiningUnit1 from './pages/MiningUnit1.jsx';

import MedicalDemoSimulator from './pages/MedicalDemoSimulator.jsx';
import MedicalExamSimulator from './pages/MedicalExamSimulator.jsx';
import MedicalPathways from './pages/MedicalPathways.jsx';
import MedicalScenario from './pages/MedicalScenario.jsx';
import MedicalUnit1 from './pages/MedicalUnit1.jsx';

import OilGasDemoSimulator from './pages/OilGasDemoSimulator.jsx';
import OilGasExamSimulator from './pages/OilGasExamSimulator.jsx';
import OilGasPathways from './pages/OilGasPathways.jsx';
import OilGasScenario from './pages/OilGasScenario.jsx';
import OilGasUnit1 from './pages/OilGasUnit1.jsx';

import AviationPilotATCDemoSimulator from './pages/AviationPilotATCDemoSimulator.jsx';
import AviationPilotATCExamSimulator from './pages/AviationPilotATCExamSimulator.jsx';
import AviationPilotATCPathways from './pages/AviationPilotATCPathways.jsx';
import AviationPilotATCScenario from './pages/AviationPilotATCScenario.jsx';
import AviationPilotATCUnit1 from './pages/AviationPilotATCUnit1.jsx';

import AestheticianDemoSimulator from './pages/AestheticianDemoSimulator.jsx';
import AestheticianExamSimulator from './pages/AestheticianExamSimulator.jsx';
import AestheticianPathways from './pages/AestheticianPathways.jsx';
import AestheticianScenario from './pages/AestheticianScenario.jsx';
import AestheticianUnit1 from './pages/AestheticianUnit1.jsx';

import LegalDemoSimulator from './pages/LegalDemoSimulator.jsx';
import LegalExamSimulator from './pages/LegalExamSimulator.jsx';
import LegalPathways from './pages/LegalPathways.jsx';
import LegalScenario from './pages/LegalScenario.jsx';
import LegalUnit1 from './pages/LegalUnit1.jsx';

import HospitalityDemoSimulator from './pages/HospitalityDemoSimulator.jsx';
import HospitalityExamSimulator from './pages/HospitalityExamSimulator.jsx';
import HospitalityPathways from './pages/HospitalityPathways.jsx';
import HospitalityScenario from './pages/HospitalityScenario.jsx';
import HospitalityUnit1 from './pages/HospitalityUnit1.jsx';

import CabinCrewDemoSimulator from './pages/CabinCrewDemoSimulator.jsx';
import CabinCrewExamSimulator from './pages/CabinCrewExamSimulator.jsx';
import CabinCrewPathways from './pages/CabinCrewPathways.jsx';
import CabinCrewScenario from './pages/CabinCrewScenario.jsx';
import CabinCrewUnit1 from './pages/CabinCrewUnit1.jsx';

const NotFoundPage = () => (
  <div className="p-10 text-center">
    <h2>404: Page Not Found</h2>
    <p>The page you are looking for does not exist.</p>
  </div>
);

export default function App() {
  const [user, setUser] = React.useState(null);
  const [authLoading, setAuthLoading] = React.useState(true);

  React.useEffect(() => {
    const { auth } = require('./firebase'); 
    const unsubscribe = auth.onAuthStateChanged((u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (authLoading) return <div className="p-10 text-center">Loading Ecosystem...</div>;

  return (
    <I18nextProvider i18n={i18n}>
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <main className="flex-grow">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/pricing" element={<PricingOptionsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/leaderlab" element={<LeaderLab />} />
            <Route path="/partner-signup" element={<PartnerSignup />} />
            <Route path="/community-investment" element={<CommunityInvestment />} />
            <Route path="/borealis" element={<BorealisPage />} />
            <Route path="/borealis/data-room" element={<DataRoomPage />} />
            
            {/* Game Routes */}
            <Route path="/autarky" element={<AutarkyGameEngine />} />
            <Route path="/veridian" element={<VeridianStart />} />
            <Route path="/veridian-engine" element={<VeridianEngine />} />
            <Route path="/cards" element={<ArgotCard />} />

            {/* Special Tracks */}
            <Route path="/young-genius/form" element={<YoungGeniusForm />} />
            <Route path="/young-genius/quiz" element={<YoungGeniusQuizPage />} />
            <Route path="/custom-track/form" element={<CustomTrackForm />} />
            <Route path="/custom-track/quiz" element={<CustomTrackQuiz />} />

            {/* PERSONAL TRACKER & WINNIE TRACKER */}
            <Route path="/pathway" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><PersonalPathway user={user} /></ProtectedRoute>} />
            
            {/* Unified CRM Route */}
            <Route path="/crm" element={
              <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                <ClientCRM teacherId={user?.uid} user={user} />
              </ProtectedRoute>
            } />
            
            {/* CORRECTED WINNIE ROUTE */}
            <Route path="/winnie" element={
              <ProtectedRoute allowedRoles={['teacher', 'admin', 'partner']}>
                <div className="space-y-6">
                    <WinnieTracker user={user} />
                    <WinnieReport user={user} />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/scheduler" element={
              <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                <PersonalChoreScheduler user={user} />
              </ProtectedRoute>
            } />
            
            {/* Protected User Routes */}
            <Route path="/my-dashboard" element={<ProtectedRoute allowedRoles={['student', 'teacher', 'partner']}><StudentDashboard /></ProtectedRoute>} />
            <Route path="/lexicon" element={<ProtectedRoute allowedRoles={['student', 'teacher', 'partner']}><LexiconPage /></ProtectedRoute>} />
            <Route path="/lexicon/practice" element={<ProtectedRoute allowedRoles={['student', 'teacher', 'partner']}><LexiconPractice /></ProtectedRoute>} />
            <Route path="/investment" element={<ProtectedRoute allowedRoles={['student', 'teacher', 'partner']}><CrowdfundingDashboard /></ProtectedRoute>} />
            <Route path="/teacher-dashboard" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherDashboard /></ProtectedRoute>} />
            <Route path="/partner-dashboard" element={<ProtectedRoute allowedRoles={['partner']}><PartnerDashboard /></ProtectedRoute>} />

            {/* --- COURSE ROUTES --- */}
            <Route path="/banking/*" element={<Routes>
              <Route path="demo" element={<BankingDemoSimulator />} />
              <Route path="exam" element={<BankingExamSimulator />} />
              <Route path="pathway" element={<BankingPathways />} />
              <Route path="scenario" element={<BankingScenario />} />
              <Route path="unit1" element={<BankingUnit1 />} />
            </Routes>} />

            <Route path="/logistics/demo" element={<LogisticsDemoSimulator />} />
            <Route path="/logistics/exam" element={<LogisticsExamSimulator />} />
            <Route path="/logistics/pathway" element={<LogisticsPathways />} />
            <Route path="/logistics/scenario" element={<LogisticsScenario />} />
            <Route path="/logistics/unit1" element={<LogisticsUnit1 />} />

            <Route path="/mining/demo" element={<MiningDemoSimulator />} />
            <Route path="/mining/exam" element={<MiningExamSimulator />} />
            <Route path="/mining/pathway" element={<MiningPathways />} />
            <Route path="/mining/scenario" element={<MiningScenario />} />
            <Route path="/mining/unit1" element={<MiningUnit1 />} />

            <Route path="/medical/demo" element={<MedicalDemoSimulator />} />
            <Route path="/medical/exam" element={<MedicalExamSimulator />} />
            <Route path="/medical/pathway" element={<MedicalPathways />} />
            <Route path="/medical/scenario" element={<MedicalScenario />} />
            <Route path="/medical/unit1" element={<MedicalUnit1 />} />

            <Route path="/oilgas/demo" element={<OilGasDemoSimulator />} />
            <Route path="/oilgas/exam" element={<OilGasExamSimulator />} />
            <Route path="/oilgas/pathway" element={<OilGasPathways />} />
            <Route path="/oilgas/scenario" element={<OilGasScenario />} />
            <Route path="/oilgas/unit1" element={<OilGasUnit1 />} />

            <Route path="/aviation/demo" element={<AviationPilotATCDemoSimulator />} />
            <Route path="/aviation/exam" element={<AviationPilotATCExamSimulator />} />
            <Route path="/aviation/pathway" element={<AviationPilotATCPathways />} />
            <Route path="/aviation/scenario" element={<AviationPilotATCScenario />} />
            <Route path="/aviation/unit1" element={<AviationPilotATCUnit1 />} />

            <Route path="/aesthetician/demo" element={<AestheticianDemoSimulator />} />
            <Route path="/aesthetician/exam" element={<AestheticianExamSimulator />} />
            <Route path="/aesthetician/pathway" element={<AestheticianPathways />} />
            <Route path="/aesthetician/scenario" element={<AestheticianScenario />} />
            <Route path="/aesthetician/unit1" element={<AestheticianUnit1 />} />

            <Route path="/legal/demo" element={<LegalDemoSimulator />} />
            <Route path="/legal/exam" element={<LegalExamSimulator />} />
            <Route path="/legal/pathway" element={<LegalPathways />} />
            <Route path="/legal/scenario" element={<LegalScenario />} />
            <Route path="/legal/unit1" element={<LegalUnit1 />} />

            <Route path="/hotel/demo" element={<HospitalityDemoSimulator />} />
            <Route path="/hotel/exam" element={<HospitalityExamSimulator />} />
            <Route path="/hotel/pathway" element={<HospitalityPathways />} />
            <Route path="/hotel/scenario" element={<HospitalityScenario />} />
            <Route path="/hotel/unit1" element={<HospitalityUnit1 />} />

            <Route path="/cabin/demo" element={<CabinCrewDemoSimulator />} />
            <Route path="/cabin/exam" element={<CabinCrewExamSimulator />} />
            <Route path="/cabin/pathway" element={<CabinCrewPathways />} />
            <Route path="/cabin/scenario" element={<CabinCrewScenario />} />
            <Route path="/cabin/unit1" element={<CabinCrewUnit1 />} />

            <Route path="/argot/:courseId" element={<ArgotPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </I18nextProvider>
  );
}